<template>
  <div class="overtime-policies-table-component">
    <div class="table-wrapper">
      <div class="scrollable">
        <AppTable
          ref="table"
          hoverable="hoverable"
          :loading="showLoading"
          :table-data="overtimePolicies"
        >
          <AppTableColumn
            v-slot="{ row }"
            header="Policy name"
          >
            <div class="d-flex align-items-center">
              <i
                v-if="row.superceded_by_id"
                class="fa fa-warning text-orange a-tooltip mr-5"
                title="Replaced"
              />
              <AppHoverLink
                :to="appHoverLinkToOvertimePolicy(row)"
                :title="appHoverLinkTitle(row)"
                @click="openEditDialog(row)"
              >
                <div class="cursor-pointer">
                  <strong>{{ row.name }}</strong>
                </div>
              </AppHoverLink>
            </div>
          </AppTableColumn>

          <AppTableColumn
            v-slot="{ row }"
            header="Summary"
          >
            <strong>After {{ row.threshold / 3600 }} hrs per week</strong>
            <div
              v-if="row.pay_rate_multiplier"
              class="text-muted"
            >
              {{ row.pay_rate_multiplier }} x member's pay rate
              <!--  @TODO: Currently not allowing specific bill rate multipliers (it is always 1)-->
              <!-- .text-grey(v-if="row.bill_rate_multiplier") {{ row.bill_rate_multiplier }} x member's bill rate-->
            </div>
          </AppTableColumn>

          <AppTableColumn
            v-slot="{ row }"
            header="Members"
          >
            <AppHoverLink
              v-if="row.can_edit || row.user_organization_ids.length"
              :title="appHoverLinkMembersTitle(row)"
              :icon="appHoverLinkIcon(row)"
              @click="openEditMembersDialog(row)"
            >
              {{ row.user_organization_ids.length }}
            </AppHoverLink>
            <span v-else>—</span>
          </AppTableColumn>

          <AppTableColumn
            v-slot="{ row }"
            header="Status"
          >
            <div
              v-tooltip="{ placement: 'right' }"
              class="d-inline-flex align-items-center mb-8 gap-8"
              :title="rowTooltip(row)"
            >
              <label
                class="label overtime-label m-0"
                :class="`overtime-${row.status === 'expiring' ? 'active' : row.status}`"
              >
                {{ (row.status === 'expiring' ? 'active' : row.status) | capitalize }}
              </label>
              <i
                v-if="row.until"
                class="hi hi-lock text-muted"
              />
              <i
                v-else-if="row.locked"
                class="fa fa-clock-o text-overtime"
              />
            </div>

            <div
              v-if="row.until"
              class="small text-muted"
            >
              <i
                v-if="row.edit_state === 'closed' || row.use_state === 'active'"
                class="fa fa-warning text-orange a-tooltip mr-5"
                title="Expiring"
              />
              {{ inThePast(row.until) ? 'Ended on:' : 'Ends on:' }} {{ row.until | formatDate }}
            </div>
            <div
              v-else-if="row.since"
              class="small text-muted"
            >
              {{ inThePast(row.since) ? 'Started on:' : 'Starts on:' }} {{ row.since | formatDate }}
            </div>
          </AppTableColumn>

          <AppTableColumn v-slot="{ row }">
            <AppDropdown
              class="pull-right table-actions-dropdown"
              direction="right"
              label="Actions"
              size="small"
            >
              <AppDropdownItem
                v-if="row.can_edit"
                @click="openEditDialog(row)"
              >
                Edit
              </AppDropdownItem>
              <AppDropdownItem
                v-else
                @click="openEditDialog(row)"
              >
                View
              </AppDropdownItem>
              <AppDropdownItem
                v-if="row.can_edit"
                @click="openEditMembersDialog(row)"
              >
                Edit members
              </AppDropdownItem>
              <AppDropdownItem
                v-else
                disabled="disabled"
                @click.stop
              >
                Edit members
              </AppDropdownItem>
              <AppDropdownItem
                v-if="row.can_archive"
                @click="onArchive(row)"
              >
                Archive
              </AppDropdownItem>
              <AppDropdownItem
                v-else
                :disabled="!row.can_restore"
                @click="onRestore(row)"
              >
                Restore
              </AppDropdownItem>
            </AppDropdown>
          </AppTableColumn>
        </AppTable>
      </div>
    </div>
    <AppPagination
      v-if="overtimePolicies.length > 0"
      :pagination="pagination"
      :resource="{ one: 'policy', many: 'policies' }"
      @page-changed="onPageChanged"
    />
    <AppConfirm
      v-if="showConfirmArchiveDialog"
      title="Archive policy"
      confirm-label="Archive policy"
      @cancel="onCancelArchive"
      @confirm="onConfirmArchive"
    >
      You’re about to archive this overtime policy. The rules of this policy will
      <strong>remain active for the rest of your organization’s week,</strong> and will be removed starting next week.
    </AppConfirm>
  </div>
</template>

<script>
import { mapInertiaProps } from '@/utils/inertia';
import { applyTemplate } from '@/utils/url_template';
import { ARCHIVE_OVERTIME_POLICIES_URL, RESTORE_OVERTIME_POLICIES_URL } from '@/app_constants';
import dayjs from 'dayjs';

export default {
  name: 'OvertimePoliciesTable',
  props: {
    allActivePolicies: {
      type: Array,
      required: true,
    },
    overtimePolicies: {
      type: Array,
      required: true,
    },
    pagination: {
      type: Object,
      required: true,
    },
    showingActive: {
      type: Boolean,
      default: true,
    },
    showLoading: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      showConfirmArchiveDialog: false,
      activePolicy: null,
    };
  },
  computed: {
    ...mapInertiaProps({
      organization: 'organization',
      members: 'members',
    }),
  },
  methods: {
    appHoverLinkToOvertimePolicy(row) {
      return applyTemplate('/overtime_policies/:id', { id: row.id });
    },
    appHoverLinkTitle(row) {
      return row.can_edit ? 'Edit this overtime policy' : 'View this overtime policy';
    },
    appHoverLinkMembersTitle(row) {
      return row.can_edit ? "Edit this overtime policy's members" : "View this overtime policy's members";
    },
    appHoverLinkIcon(row) {
      return row.can_edit ? undefined : 'hi hi-eye';
    },
    inThePast(date) {
      return dayjs(date).isBefore(dayjs());
    },
    rowTooltip(row) {
      if (!row.can_edit) {
        return 'Policy is set to expire and cannot be edited';
      }
      if (row.locked) {
        return 'Overtime logged. Any edits will produce a duplicate policy.';
      }

      return 'Policy can be edited. No overtime has been logged';
    },
    onPageChanged(page) {
      this.$emit('page-changed', page);
    },
    openEditDialog(policy) {
      this.$emit('open-edit-dialog', policy);
    },
    openEditMembersDialog(policy) {
      this.$emit('open-edit-members-dialog', policy);
    },
    onArchive(policy) {
      this.activePolicy = policy;
      if (policy.can_edit) {
        return this.onConfirmArchive();
      }
      this.showConfirmArchiveDialog = true;
    },
    onCancelArchive() {
      this.showConfirmArchiveDialog = false;
    },
    onConfirmArchive() {
      this.showConfirmArchiveDialog = false;
      this.submitStatusUpdate(this.activePolicy, ARCHIVE_OVERTIME_POLICIES_URL);
    },
    onRestore(policy) {
      const confirmation = 'Do you want to restore this policy?';
      const activePolicies = [];

      // When trying to restore a policy, check that its members are not present on any other active policy.
      this.allActivePolicies
        .filter((ap) => ap.id !== policy.id)
        .forEach((ap) => {
          const inPolicy = ap.user_organization_ids.filter((element) => policy.user_organization_ids.includes(element));

          if (inPolicy.length) {
            activePolicies.push({ name: ap.name, memberIds: inPolicy, members: [] });
          }
        });

      // If any active policies are preventing restoring the policy, show an alert containing the list of policies
      // and members.
      if (activePolicies.length) {
        const policyErrorMessages = [
          `Could not restore the policy because it contains members on other active overtime polices:`,
          ' ',
        ];

        activePolicies.forEach((ap) => {
          ap.members.push(...this.members.filter((member) => ap.memberIds.includes(member.id)));
          policyErrorMessages.push(`${ap.name}: ${ap.members.map((member) => member.name).join(', ')}`);
        });

        alert(policyErrorMessages.join('\n'));
      } else {
        if (confirm(confirmation)) {
          this.submitStatusUpdate(policy, RESTORE_OVERTIME_POLICIES_URL);
        }
      }
    },
    submitStatusUpdate(policy, urlTemplate) {
      const { origin } = window.location;
      const overtimePoliciesUrlString = new URL(applyTemplate(urlTemplate, { id: policy.id }), origin).toString();

      this.$inertia.post(overtimePoliciesUrlString, { organization_id: this.organization.id });
    },
  },
};
</script>
